import type { Edge } from '@xyflow/react';

// ─── Couleurs par couche technique (badge dans DetailPanel) ───────────────
export const LAYER_COLORS = {
  external: { bg: '#7c3aed', border: '#5b21b6', text: '#fff' },
  api:      { bg: '#dc2626', border: '#991b1b', text: '#fff' },
  utils:    { bg: '#0369a1', border: '#075985', text: '#fff' },
  core:     { bg: '#d97706', border: '#b45309', text: '#fff' },
  ui:       { bg: '#059669', border: '#047857', text: '#fff' },
  hooks:    { bg: '#0891b2', border: '#0e7490', text: '#fff' },
};

// ─── Domaines métier (regroupement visuel pliable/dépliable) ─────────────
export type DomainId =
  | 'dom_external' | 'dom_api' | 'dom_cloud' | 'dom_premium' | 'dom_audio_midi'
  | 'dom_chart' | 'dom_guitar' | 'dom_jam' | 'dom_nav' | 'dom_pdf'
  | 'dom_hooks' | 'dom_mididaw' | 'dom_devtools' | 'dom_landing';

export const DOMAINS: Record<DomainId, { label: string; bg: string; border: string; text: string; description: string }> = {
  dom_external:   { label: 'Services Externes',          bg: '#7c3aed', border: '#5b21b6', text: '#fff', description: 'Tout ce qui dépend d\'un service tiers : connexion (Firebase), paiements (Stripe), sauvegarde cloud (Google Drive), emails (SendGrid), hébergement (Vercel).' },
  dom_api:        { label: 'Routes API (Vercel)',         bg: '#dc2626', border: '#991b1b', text: '#fff', description: 'Les petites fonctions serveur qui tournent sur Vercel : paiements, emails, vérification d\'abonnement, jam sessions.' },
  dom_cloud:      { label: 'Cloud & Persistance',         bg: '#0369a1', border: '#075985', text: '#fff', description: 'Sauvegarde et synchronisation : stockage local hors-ligne, synchronisation Google Drive, connexion utilisateur.' },
  dom_premium:    { label: 'Abonnement & Premium',        bg: '#b45309', border: '#92400e', text: '#fff', description: 'Tout ce qui concerne l\'abonnement Premium : tarifs, ce qui est débloqué ou non, écrans d\'upgrade et de gestion d\'abonnement.' },
  dom_audio_midi: { label: 'Audio & MIDI',                bg: '#0d9488', border: '#0f766e', text: '#fff', description: 'Le son des accords et la connexion à un clavier MIDI : lecture audio, reconnaissance d\'accords joués, contrôles de lecture.' },
  dom_chart:      { label: 'Édition de Chart',            bg: '#d97706', border: '#b45309', text: '#fff', description: 'Le cœur de l\'app : la grille de mesures, les accords, les annotations, le chargement/sauvegarde d\'un morceau, les set lists.' },
  dom_guitar:     { label: 'Guitare',                     bg: '#65a30d', border: '#4d7c0f', text: '#fff', description: 'Les diagrammes de frettes guitare et l\'explorateur de positions d\'accords.' },
  dom_jam:        { label: 'Jam Session',                 bg: '#db2777', border: '#be185d', text: '#fff', description: 'Le mode collaboratif temps réel : partager un chart en direct avec d\'autres musiciens à distance.' },
  dom_nav:        { label: 'Menus & Navigation',          bg: '#4f46e5', border: '#4338ca', text: '#fff', description: 'Les menus de l\'app : menu d\'édition, menu de performance, panneau de réglages, aide, mentions légales.' },
  dom_pdf:        { label: 'Export PDF',                  bg: '#475569', border: '#334155', text: '#fff', description: 'L\'export d\'un chart en PDF imprimable.' },
  dom_hooks:      { label: 'Hooks (State Management)',    bg: '#0891b2', border: '#0e7490', text: '#fff', description: 'La mémoire interne de l\'app — tout ce qui garde en tête l\'état courant (le chart ouvert, les modales, la sauvegarde auto...). Technique, mais souvent le bon point d\'entrée pour un changement de comportement.' },
  dom_mididaw:    { label: 'MIDI-DAW (Piano Recorder)',   bg: '#a21caf', border: '#86198f', text: '#fff', description: 'Le studio d\'enregistrement piano intégré : enregistrement MIDI, piano roll, partition, lecture des instruments.' },
  dom_devtools:   { label: 'Outils de Développement',     bg: '#57534e', border: '#44403c', text: '#fff', description: 'Outils visibles seulement en dev (jamais pour tes utilisateurs) : cette carte elle-même, l\'overlay de debug visuel, le debugger de connexion Jam Session.' },
  dom_landing:    { label: 'Page d\'accueil (Landing)',    bg: '#f97316', border: '#c2410c', text: '#fff', description: 'Le tout premier écran vu par un visiteur ou un utilisateur non connecté, avant d\'entrer dans l\'app : présentation du produit, et choix New (Chord Grid vs Piano Rec) / Open / Setlists / Jam Session.' },
};


export const DOMAIN_ORDER: DomainId[] = [
  'dom_landing', 'dom_chart', 'dom_nav', 'dom_hooks', 'dom_audio_midi', 'dom_mididaw',
  'dom_guitar', 'dom_jam', 'dom_cloud', 'dom_premium', 'dom_external', 'dom_api', 'dom_pdf',
  'dom_devtools',
];

// ─── Détails affichés dans le panneau latéral ─────────────────────────────
export interface NodeDetail {
  id: string;
  label: string;
  layer: keyof typeof LAYER_COLORS;
  domain: DomainId;
  role: string;
  connectedServices: string[];
  keyFiles: string[];
}

export const NODE_DETAILS: Record<string, NodeDetail> = {
  // ── Services externes ──────────────────────────────────────────────────
  svc_firebase: {
    id: 'svc_firebase', label: 'Firebase', layer: 'external', domain: 'dom_external',
    role: 'Authentification utilisateurs (Google + email/password) + Firestore pour les préférences Premium',
    connectedServices: ['Google OAuth'],
    keyFiles: ['utils/firebase.ts', 'contexts/AuthContext.tsx', 'api/utils/firebase-admin.js'],
  },
  svc_stripe: {
    id: 'svc_stripe', label: 'Stripe', layer: 'external', domain: 'dom_external',
    role: 'Gestion des paiements et abonnements Premium',
    connectedServices: ['Stripe Webhooks'],
    keyFiles: ['api/stripe/create-checkout.js', 'api/stripe/webhook.js', 'api/stripe/create-portal-session.js', 'utils/pricing.ts'],
  },
  svc_drive: {
    id: 'svc_drive', label: 'Google Drive', layer: 'external', domain: 'dom_external',
    role: 'Synchronisation cloud des charts entre appareils (Premium)',
    connectedServices: ['Google Drive API v3', 'Firebase (identité)'],
    keyFiles: ['utils/drive.ts'],
  },
  svc_sendgrid: {
    id: 'svc_sendgrid', label: 'SendGrid', layer: 'external', domain: 'dom_external',
    role: 'Envoi des emails du formulaire de contact',
    connectedServices: ['SendGrid API'],
    keyFiles: ['api/contact/send-message.js'],
  },
  svc_vercel: {
    id: 'svc_vercel', label: 'Vercel', layer: 'external', domain: 'dom_external',
    role: 'Hébergement de l\'app front-end + déploiement automatique depuis master (git push → prod). Exécute les 8 routes API comme fonctions serverless (Node.js isolé par requête, pas de serveur permanent).',
    connectedServices: ['GitHub (auto-deploy)', 'Toutes les routes /api/'],
    keyFiles: ['vercel.json', 'api/health.js', 'api/signaling.js', 'api/contact/', 'api/stripe/', 'api/user/'],
  },

  // ── API Routes ─────────────────────────────────────────────────────────
  api_health: {
    id: 'api_health', label: '/api/health', layer: 'api', domain: 'dom_api',
    role: 'Vérification santé du serveur — confirme que Stripe et Firebase sont configurés',
    connectedServices: ['Stripe', 'Firebase'],
    keyFiles: ['api/health.js'],
  },
  api_contact: {
    id: 'api_contact', label: '/api/contact', layer: 'api', domain: 'dom_api',
    role: 'Reçoit le formulaire de contact et envoie un email via SendGrid (rate-limit: 5/min par IP)',
    connectedServices: ['SendGrid'],
    keyFiles: ['api/contact/send-message.js'],
  },
  api_checkout: {
    id: 'api_checkout', label: '/api/stripe/checkout', layer: 'api', domain: 'dom_api',
    role: 'Crée une session de paiement Stripe pour l\'abonnement Premium (rate-limit: 10/min)',
    connectedServices: ['Stripe', 'Firebase'],
    keyFiles: ['api/stripe/create-checkout.js', 'api/utils/rateLimit.js'],
  },
  api_portal: {
    id: 'api_portal', label: '/api/stripe/portal', layer: 'api', domain: 'dom_api',
    role: 'Crée une session portail Stripe pour gérer son abonnement (annuler, changer carte...)',
    connectedServices: ['Stripe', 'Firebase'],
    keyFiles: ['api/stripe/create-portal-session.js'],
  },
  api_webhook: {
    id: 'api_webhook', label: '/api/stripe/webhook', layer: 'api', domain: 'dom_api',
    role: 'Reçoit les événements Stripe (paiement réussi, annulation) et met à jour Firestore',
    connectedServices: ['Stripe', 'Firebase Firestore'],
    keyFiles: ['api/stripe/webhook.js', 'api/utils/firebase-admin.js'],
  },
  api_subscription: {
    id: 'api_subscription', label: '/api/user/subscription', layer: 'api', domain: 'dom_api',
    role: 'Retourne le statut d\'abonnement de l\'utilisateur connecté (GET, auth requise)',
    connectedServices: ['Firebase', 'Stripe'],
    keyFiles: ['api/user/subscription.js', 'api/utils/firebaseAuth.js'],
  },
  api_signaling: {
    id: 'api_signaling', label: '/api/signaling', layer: 'api', domain: 'dom_api',
    role: 'Serveur de signalisation WebRTC stateless pour les Jam Sessions (timeout 30 min)',
    connectedServices: ['WebRTC'],
    keyFiles: ['api/signaling.js'],
  },
  api_testenv: {
    id: 'api_testenv', label: '/api/test-env', layer: 'api', domain: 'dom_api',
    role: 'Diagnostic des variables d\'environnement Vercel (dev uniquement)',
    connectedServices: [],
    keyFiles: ['api/test-env.js'],
  },

  // ── Cloud & Persistance ─────────────────────────────────────────────────
  util_db: {
    id: 'util_db', label: 'db.ts', layer: 'utils', domain: 'dom_cloud',
    role: 'Persistance locale offline-first avec IndexedDB (idb v8). Gère charts, set lists, préférences',
    connectedServices: ['IndexedDB (navigateur)'],
    keyFiles: ['utils/db.ts'],
  },
  util_drive: {
    id: 'util_drive', label: 'drive.ts', layer: 'utils', domain: 'dom_cloud',
    role: 'Synchronisation Google Drive — auth popup OAuth, lecture/écriture des charts JSON',
    connectedServices: ['Google Drive API', 'Firebase'],
    keyFiles: ['utils/drive.ts'],
  },
  util_firebase: {
    id: 'util_firebase', label: 'firebase.ts', layer: 'utils', domain: 'dom_cloud',
    role: 'Initialisation Firebase, auth Google/email, lecture/écriture Firestore, gestion tokens',
    connectedServices: ['Firebase Auth', 'Firebase Firestore'],
    keyFiles: ['utils/firebase.ts'],
  },
  ctx_auth: {
    id: 'ctx_auth', label: 'AuthContext', layer: 'utils', domain: 'dom_cloud',
    role: 'Context React global — état d\'authentification, userProfile, méthodes login/logout',
    connectedServices: ['Firebase'],
    keyFiles: ['contexts/AuthContext.tsx', 'utils/firebase.ts'],
  },
  ui_auth: {
    id: 'ui_auth', label: 'Auth / UnifiedAuth', layer: 'ui', domain: 'dom_cloud',
    role: 'Modals de connexion/inscription — Google OAuth et email/password',
    connectedServices: ['Firebase'],
    keyFiles: ['components/Auth.tsx', 'components/UnifiedAuth.tsx'],
  },

  // ── Abonnement & Premium ────────────────────────────────────────────────
  util_features: {
    id: 'util_features', label: 'features.ts', layer: 'utils', domain: 'dom_premium',
    role: 'Feature gates Premium — définit quelles fonctions sont accessibles selon l\'abonnement',
    connectedServices: [],
    keyFiles: ['utils/features.ts', 'utils/freemiumLimits.ts', 'contexts/SubscriptionContext.tsx'],
  },
  util_pricing: {
    id: 'util_pricing', label: 'pricing.ts', layer: 'utils', domain: 'dom_premium',
    role: 'Constantes de pricing, tiers d\'abonnement, calcul des limites freemium',
    connectedServices: ['Stripe'],
    keyFiles: ['utils/pricing.ts'],
  },
  ctx_subscription: {
    id: 'ctx_subscription', label: 'SubscriptionContext', layer: 'utils', domain: 'dom_premium',
    role: 'Context React global — état Premium, hasFeature(), chargement abonnement Stripe',
    connectedServices: ['Stripe', 'Firebase'],
    keyFiles: ['contexts/SubscriptionContext.tsx', 'utils/features.ts'],
  },
  ui_upgrade: {
    id: 'ui_upgrade', label: 'UpgradeModal', layer: 'ui', domain: 'dom_premium',
    role: 'Modal d\'upgrade Premium — tableau comparatif Free vs Premium, bouton checkout Stripe',
    connectedServices: ['Stripe'],
    keyFiles: ['components/UpgradeModal.tsx', 'components/EmbeddedCheckout.tsx', 'components/FeatureLimitModal.tsx'],
  },
  ui_subscription_mgmt: {
    id: 'ui_subscription_mgmt', label: 'SubscriptionManagement', layer: 'ui', domain: 'dom_premium',
    role: 'Panneau de gestion d\'abonnement — résumé Premium, bouton portail Stripe, annulation',
    connectedServices: ['Stripe'],
    keyFiles: ['components/SubscriptionManagement.tsx', 'components/PremiumIndicator.tsx'],
  },

  // ── Audio & MIDI ────────────────────────────────────────────────────────
  util_audio: {
    id: 'util_audio', label: 'audio.ts', layer: 'utils', domain: 'dom_audio_midi',
    role: 'Synthèse sonore des accords avec Web Audio API, enveloppes ADSR, lecture de progressions. Tous les sons de l\'Editor passent par un master gain partagé (muteEditorAudio/unmuteEditorAudio) pour pouvoir être coupés d\'un coup pendant que le Piano Recorder (MiniDaw) est ouvert.',
    connectedServices: ['Web Audio API'],
    keyFiles: ['utils/audio.ts', 'utils/audio/'],
  },
  util_midi: {
    id: 'util_midi', label: 'midi.ts', layer: 'utils', domain: 'dom_audio_midi',
    role: 'Gestion des entrées/sorties MIDI, parsing des accords depuis signal MIDI, export MIDI',
    connectedServices: ['Web MIDI API'],
    keyFiles: ['utils/midi.ts'],
  },
  ui_midi: {
    id: 'ui_midi', label: 'MidiPanel', layer: 'ui', domain: 'dom_audio_midi',
    role: 'Panneau MIDI — sélection interface MIDI, affichage signal entrant, accord détecté',
    connectedServices: ['Web MIDI API'],
    keyFiles: ['components/MidiPanel.tsx', 'components/MIDIDebugger.tsx', 'utils/midi.ts'],
  },
  ui_transport: {
    id: 'ui_transport', label: 'TransportControls', layer: 'ui', domain: 'dom_audio_midi',
    role: 'Contrôles de lecture audio — play, stop, tempo, volume, lecture des accords',
    connectedServices: ['Web Audio API'],
    keyFiles: ['components/TransportControls.tsx', 'utils/audio.ts'],
  },

  // ── Édition de Chart ────────────────────────────────────────────────────
  core_app: {
    id: 'core_app', label: 'App.tsx', layer: 'core', domain: 'dom_chart',
    role: 'Composant racine — orchestrateur principal. Gère l\'état global, les modes (Edition/Gig/Jam), l\'auto-save, et le rendu conditionnel de tous les composants',
    connectedServices: ['Tous'],
    keyFiles: ['App.tsx', 'types.ts', 'index.tsx'],
  },
  ui_chartgrid: {
    id: 'ui_chartgrid', label: 'ChartGrid', layer: 'ui', domain: 'dom_chart',
    role: 'Grille principale des mesures en mode Edition — affiche et gère toutes les mesures',
    connectedServices: [],
    keyFiles: ['components/ChartGrid.tsx', 'components/ChartGridAdvanced.tsx'],
  },
  ui_measuregrid: {
    id: 'ui_measuregrid', label: 'MeasureGrid', layer: 'ui', domain: 'dom_chart',
    role: 'Grille d\'une mesure individuelle avec ses cases d\'accords, annotations et paroles',
    connectedServices: [],
    keyFiles: ['components/MeasureGrid.tsx', 'components/Measure.tsx', 'components/ChordCell.tsx'],
  },
  ui_annotations: {
    id: 'ui_annotations', label: 'MeasureAnnotations', layer: 'ui', domain: 'dom_chart',
    role: 'Barres de répétition, sections (A, B, Chorus...), marqueurs de structure du chart',
    connectedServices: [],
    keyFiles: ['components/MeasureAnnotations.tsx'],
  },
  ui_loadchart: {
    id: 'ui_loadchart', label: 'LoadChartModal', layer: 'ui', domain: 'dom_chart',
    role: 'Modal de chargement — liste des charts sauvegardés localement (et Drive pour Premium)',
    connectedServices: ['IndexedDB', 'Google Drive'],
    keyFiles: ['components/LoadChartModal.tsx'],
  },
  ui_collections: {
    id: 'ui_collections', label: 'Collections / Set Lists', layer: 'ui', domain: 'dom_chart',
    role: 'Gestion des set lists — collections de charts ordonnés pour les concerts (Premium). ChartPicker affiche un badge 🎹 pour les charts qui ont une partition MiniDaw enregistrée (recordedNotes).',
    connectedServices: ['IndexedDB', 'Google Drive'],
    keyFiles: ['components/CollectionManager.tsx', 'components/collections/', 'utils/db.ts'],
  },
  util_progressions: {
    id: 'util_progressions', label: 'progressions.ts', layer: 'utils', domain: 'dom_chart',
    role: 'Logique de progressions harmoniques, suggestions d\'accords, analyse tonale',
    connectedServices: [],
    keyFiles: ['utils/progressions.ts'],
  },

  // ── Guitare ──────────────────────────────────────────────────────────────
  util_guitar: {
    id: 'util_guitar', label: 'guitarChords.ts', layer: 'utils', domain: 'dom_guitar',
    role: 'Base de données des voicings guitare, calcul des positions de frettes par accord',
    connectedServices: [],
    keyFiles: ['utils/guitarChords.ts'],
  },
  ui_guitar: {
    id: 'ui_guitar', label: 'GuitarChordNavigator', layer: 'ui', domain: 'dom_guitar',
    role: 'Explorateur de voicings guitare — diagrammes de frettes, navigation entre positions',
    connectedServices: [],
    keyFiles: ['components/GuitarChordNavigator.tsx', 'components/GuitarFretboard.tsx', 'components/ChordInspector.tsx', 'utils/guitarChords.ts'],
  },

  // ── Jam Session ──────────────────────────────────────────────────────────
  util_webrtc: {
    id: 'util_webrtc', label: 'webrtc.ts', layer: 'utils', domain: 'dom_jam',
    role: 'Connexions WebRTC peer-to-peer pour partager un chart en temps réel (Jam Sessions)',
    connectedServices: ['WebRTC', '/api/signaling'],
    keyFiles: ['utils/webrtc.ts'],
  },
  ui_jam: {
    id: 'ui_jam', label: 'JamSession', layer: 'ui', domain: 'dom_jam',
    role: 'Interface de jam session — connexion peer-to-peer, affichage chart partagé en temps réel',
    connectedServices: ['WebRTC', '/api/signaling'],
    keyFiles: ['components/JamHeader.tsx', 'components/JamSessionControls.tsx', 'components/JamSessionLoadingModal.tsx', 'utils/webrtc.ts'],
  },

  // ── Menus & Navigation ───────────────────────────────────────────────────
  ui_mainmenu: {
    id: 'ui_mainmenu', label: 'MainMenu', layer: 'ui', domain: 'dom_nav',
    role: 'Menu du mode Edition — hamburger mobile, toutes les actions (nouveau, charger, exporter...)',
    connectedServices: [],
    keyFiles: ['components/menu/MainMenu.tsx', 'components/menu/HeaderButtons.tsx'],
  },
  ui_gigmenu: {
    id: 'ui_gigmenu', label: 'GigMenu', layer: 'ui', domain: 'dom_nav',
    role: 'Menu du mode Performance (Gig) — navigation set list, transposition, plein écran. Bouton "Voir la partition" (visible seulement si le morceau a un enregistrement MiniDaw) qui ouvre le Piano Recorder directement en plein écran partition.',
    connectedServices: [],
    keyFiles: ['components/menu/GigMenu.tsx', 'components/GigHeader.tsx'],
  },
  ui_settings: {
    id: 'ui_settings', label: 'SettingsPanel', layer: 'ui', domain: 'dom_nav',
    role: 'Panneau de paramètres — grille, taille de police, taille des accords en Gig/Jam, debug overlay...',
    connectedServices: [],
    keyFiles: ['components/SettingsPanel.tsx'],
  },
  ui_help: {
    id: 'ui_help', label: 'HelpModal', layer: 'ui', domain: 'dom_nav',
    role: 'Guide d\'utilisation intégré — 12 sections couvrant toutes les fonctionnalités',
    connectedServices: [],
    keyFiles: ['components/HelpModal.tsx'],
  },
  ui_legal: {
    id: 'ui_legal', label: 'Légal (CGV, CGU, Mentions)', layer: 'ui', domain: 'dom_nav',
    role: 'Modals des informations légales — CGV, CGU, mentions légales, politique de confidentialité',
    connectedServices: [],
    keyFiles: ['components/LegalNoticeModal.tsx', 'components/PrivacyPolicyModal.tsx', 'components/TermsOfServiceModal.tsx'],
  },

  // ── Export PDF ────────────────────────────────────────────────────────────
  util_pdf: {
    id: 'util_pdf', label: 'pdfExport.ts', layer: 'utils', domain: 'dom_pdf',
    role: 'Export du chart courant en PDF via html2canvas et génération de PDF navigateur',
    connectedServices: [],
    keyFiles: ['utils/pdfExport.ts'],
  },
  ui_pdf: {
    id: 'ui_pdf', label: 'PDFExportModal', layer: 'ui', domain: 'dom_pdf',
    role: 'Modal d\'export PDF — aperçu + options de mise en page avant génération',
    connectedServices: [],
    keyFiles: ['components/PDFExportModal.tsx', 'utils/pdfExport.ts'],
  },

  // ── Hooks (State Management) ────────────────────────────────────────────
  hook_chartstate: {
    id: 'hook_chartstate', label: 'useChartState', layer: 'hooks', domain: 'dom_hooks',
    role: 'État du chart courant — mesures, accords, tonalité, transposition, undo, édition',
    connectedServices: [],
    keyFiles: ['hooks/useChartState.ts'],
  },
  hook_autosave: {
    id: 'hook_autosave', label: 'useAutoSave', layer: 'hooks', domain: 'dom_hooks',
    role: 'Auto-sauvegarde locale (IndexedDB) et cloud (Drive) avec debounce et suivi des changements',
    connectedServices: ['IndexedDB', 'Google Drive'],
    keyFiles: ['hooks/useAutoSave.ts'],
  },
  hook_modalstate: {
    id: 'hook_modalstate', label: 'useModalState', layer: 'hooks', domain: 'dom_hooks',
    role: 'État centralisé de toutes les modales/dialogues de l\'app (ouverture/fermeture)',
    connectedServices: [],
    keyFiles: ['hooks/useModalState.ts'],
  },
  hook_midihandlers: {
    id: 'hook_midihandlers', label: 'useMidiHandlers', layer: 'hooks', domain: 'dom_hooks',
    role: 'Accès Web MIDI, liste des périphériques, reconnaissance d\'accords en temps réel',
    connectedServices: ['Web MIDI API'],
    keyFiles: ['hooks/useMidiHandlers.ts'],
  },
  hook_drivesync: {
    id: 'hook_drivesync', label: 'useDriveSync', layer: 'hooks', domain: 'dom_hooks',
    role: 'Synchronisation Google Drive — connexion, déconnexion, full sync des charts/collections',
    connectedServices: ['Google Drive'],
    keyFiles: ['hooks/useDriveSync.ts'],
  },
  hook_jamsession: {
    id: 'hook_jamsession', label: 'useJamSession', layer: 'hooks', domain: 'dom_hooks',
    role: 'Gestion de session Jam — host/client, code de session, état de connexion WebRTC',
    connectedServices: ['WebRTC'],
    keyFiles: ['hooks/useJamSession.ts'],
  },
  hook_responsivelayout: {
    id: 'hook_responsivelayout', label: 'useResponsiveLayout', layer: 'hooks', domain: 'dom_hooks',
    role: 'Détection mobile/tablette/desktop et adaptation de la mise en page',
    connectedServices: [],
    keyFiles: ['hooks/useResponsiveLayout.ts'],
  },
  hook_chartcrud: {
    id: 'hook_chartcrud', label: 'useChartCRUD', layer: 'hooks', domain: 'dom_hooks',
    role: 'Opérations CRUD sur les charts — créer, sauvegarder, charger, supprimer, dupliquer',
    connectedServices: ['IndexedDB', 'Google Drive'],
    keyFiles: ['hooks/useChartCRUD.ts'],
  },
  hook_setlistcrud: {
    id: 'hook_setlistcrud', label: 'useSetListCRUD', layer: 'hooks', domain: 'dom_hooks',
    role: 'Opérations CRUD sur les set lists (collections de charts pour concerts)',
    connectedServices: ['IndexedDB', 'Google Drive'],
    keyFiles: ['hooks/useSetListCRUD.ts'],
  },
  hook_stripehandlers: {
    id: 'hook_stripehandlers', label: 'useStripeHandlers', layer: 'hooks', domain: 'dom_hooks',
    role: 'Déclenche les sessions Stripe Checkout et portail de gestion d\'abonnement',
    connectedServices: ['Stripe'],
    keyFiles: ['hooks/useStripeHandlers.ts'],
  },
  hook_gignavigation: {
    id: 'hook_gignavigation', label: 'useGigNavigation', layer: 'hooks', domain: 'dom_hooks',
    role: 'Navigation entre morceaux d\'une set list en mode Gig/Performance',
    connectedServices: [],
    keyFiles: ['hooks/useGigNavigation.ts'],
  },
  hook_chordinspector: {
    id: 'hook_chordinspector', label: 'useChordInspector', layer: 'hooks', domain: 'dom_hooks',
    role: 'État de l\'inspecteur d\'accord (piano/guitare) affiché au survol/clic d\'un accord',
    connectedServices: [],
    keyFiles: ['hooks/useChordInspector.ts'],
  },
  hook_measureedit: {
    id: 'hook_measureedit', label: 'useMeasureEdit', layer: 'hooks', domain: 'dom_hooks',
    role: 'Édition fine d\'une mesure — copier/coller, insertion, suppression, subdivision',
    connectedServices: [],
    keyFiles: ['hooks/useMeasureEdit.ts'],
  },
  hook_audioplayback: {
    id: 'hook_audioplayback', label: 'useAudioPlayback', layer: 'hooks', domain: 'dom_hooks',
    role: 'Lecture audio du chart — play/stop, curseur de mesure courante, métronome',
    connectedServices: ['Web Audio API'],
    keyFiles: ['hooks/useAudioPlayback.ts'],
  },

  // ── MIDI-DAW (Piano Recorder) ────────────────────────────────────────────
  ui_mididaw_modal: {
    id: 'ui_mididaw_modal', label: 'MidiDawModal', layer: 'ui', domain: 'dom_mididaw',
    role: 'Pont entre l\'état de Chord Notator (bpm, signature, input MIDI partagé, AudioContext) et le module MiniDaw. Expose aussi le pane "Chart" (ChartGrid réel du host, lecture seule) avec auto-remplissage non destructif des accords détectés (bloc ou arpège) vers les mesures vides, et mute l\'audio de l\'Editor pendant que le DAW est ouvert (et inversement) pour éviter le mélange des deux moteurs de son.',
    connectedServices: ['Web MIDI API', 'Web Audio API'],
    keyFiles: ['components/MidiDawModal.tsx'],
  },
  mididaw_core: {
    id: 'mididaw_core', label: 'MiniDaw core/', layer: 'utils', domain: 'dom_mididaw',
    role: 'Moteur audio 480 PPQ, timing/quantification, détection d\'accords, export MIDI/MusicXML, accès Web MIDI',
    connectedServices: ['Web MIDI API', 'Web Audio API'],
    keyFiles: ['features/midi-daw/core/audioEngine.ts', 'features/midi-daw/core/timing.ts', 'features/midi-daw/core/quantize.ts', 'features/midi-daw/core/chordDetector.ts', 'features/midi-daw/core/scoreTranscriber.ts', 'features/midi-daw/core/midiFileExport.ts', 'features/midi-daw/core/midiAccess.ts'],
  },
  mididaw_hooks: {
    id: 'mididaw_hooks', label: 'MiniDaw hooks/', layer: 'hooks', domain: 'dom_mididaw',
    role: 'Machine \u00e0 \u00e9tats de transport (rec/play/stop/undo-redo) et \u00e9diteur de piano roll \u2014 outils d\u2019\u00e9dition : S\u00e9lection (clic/marquee/d\u00e9placer/redimensionner), Gomme (supprimer au clic/glisser), Rasoir (scinder une note en deux au point cliqu\u00e9).',
    connectedServices: [],
    keyFiles: ['features/midi-daw/hooks/useMidiRecorder.ts', 'features/midi-daw/hooks/usePianoRollEditor.ts'],
  },
  mididaw_components: {
    id: 'mididaw_components', label: 'MiniDaw components/', layer: 'ui', domain: 'dom_mididaw',
    role: 'Studio complet — transport, piano roll, partition, lead sheet, clavier virtuel, export',
    connectedServices: [],
    keyFiles: ['features/midi-daw/components/MiniDaw.tsx', 'features/midi-daw/components/PianoRoll.tsx', 'features/midi-daw/components/PianoScoreView.tsx', 'features/midi-daw/components/TransportBar.tsx'],
  },
  mididaw_audio: {
    id: 'mididaw_audio', label: 'MiniDaw audio/', layer: 'utils', domain: 'dom_mididaw',
    role: 'Instruments échantillonnés (Soundfont MusyngKite MP3), synthé de secours, chaîne d\'effets (reverb)',
    connectedServices: ['smplr (Soundfont MP3 CDN)'],
    keyFiles: ['features/midi-daw/audio/InstrumentManager.ts', 'features/midi-daw/audio/instruments/', 'features/midi-daw/audio/effects/', 'features/midi-daw/audio/decodeGuard.ts'],
  },

  // ── Outils de développement ──────────────────────────────────────────────
  dev_arch_explorer: {
    id: 'dev_arch_explorer', label: 'Architecture Explorer', layer: 'ui', domain: 'dom_devtools',
    role: 'Cette carte elle-même — visualisation interactive de l\'app, accessible via #arch, avec file d\'attente de questions vers Copilot',
    connectedServices: [],
    keyFiles: ['components/ArchitectureExplorer/ArchitecturePage.tsx', 'components/ArchitectureExplorer/archData.ts', 'components/ArchitectureExplorer/layout.ts', 'components/ArchitectureExplorer/DomainSidebar.tsx', 'components/ArchitectureExplorer/DetailPanel.tsx', 'components/ArchitectureExplorer/archQueue.ts', 'vite.config.ts', 'arch-queue.json'],
  },
  dev_debug_overlay: {
    id: 'dev_debug_overlay', label: 'DebugOverlay', layer: 'ui', domain: 'dom_devtools',
    role: 'Surcouche visuelle de debug (niveaux 1 à 6) qui annote les composants à l\'écran pour diagnostiquer des bugs d\'affichage',
    connectedServices: [],
    keyFiles: ['components/DebugOverlay.tsx'],
  },
  dev_webrtc_debugger: {
    id: 'dev_webrtc_debugger', label: 'WebRTCDebugger', layer: 'ui', domain: 'dom_devtools',
    role: 'Panneau de diagnostic des connexions Jam Session (état WebRTC, pairs connectés, signalisation)',
    connectedServices: ['WebRTC'],
    keyFiles: ['components/WebRTCDebugger.tsx'],
  },

  // ── Page d'accueil (Landing) ─────────────────────────────────────────────
  ui_landing_desktop: {
    id: 'ui_landing_desktop', label: 'LandingPageDesktop', layer: 'ui', domain: 'dom_landing',
    role: 'Page d\'accueil desktop/tablette (source Figma) — hero, présentation des fonctionnalités, menu New (dropdown Chord Grid / Piano Rec), Open, Setlists, Jam Session.',
    connectedServices: [],
    keyFiles: ['components/LandingPageDesktop.tsx'],
  },
  ui_landing_mobile: {
    id: 'ui_landing_mobile', label: 'WelcomeScreen (mobile)', layer: 'ui', domain: 'dom_landing',
    role: 'Page d\'accueil mobile — mêmes choix qu\'en desktop (New/Open/Setlists/Jam Session) adaptés à un écran tactile étroit ; le choix New passe par le modal partagé NewChartChoiceModal plutôt qu\'un dropdown.',
    connectedServices: [],
    keyFiles: ['components/WelcomeScreen.tsx'],
  },
  ui_new_chart_choice: {
    id: 'ui_new_chart_choice', label: 'NewChartChoiceModal', layer: 'ui', domain: 'dom_landing',
    role: 'Modal partagé "Chord Grid vs Piano Rec" affiché au clic sur New en mobile (le desktop utilise un dropdown dédié dans LandingPageDesktop à la place, pas ce modal).',
    connectedServices: [],
    keyFiles: ['components/NewChartChoiceModal.tsx'],
  },
};

// ─── Arêtes (connexions) ───────────────────────────────────────────────────
function edge(source: string, target: string, animated = false): Edge {
  return {
    id: `${source}->${target}`,
    source,
    target,
    animated,
    style: { stroke: '#94a3b8', strokeWidth: 1.5 },
  };
}

export const INITIAL_EDGES: Edge[] = [
  // Vercel héberge toutes les routes API (fonctions serverless)
  edge('svc_vercel', 'api_health'),
  edge('svc_vercel', 'api_contact'),
  edge('svc_vercel', 'api_checkout'),
  edge('svc_vercel', 'api_portal'),
  edge('svc_vercel', 'api_webhook'),
  edge('svc_vercel', 'api_subscription'),
  edge('svc_vercel', 'api_signaling'),
  edge('svc_vercel', 'api_testenv'),

  // Externes → API
  edge('svc_firebase', 'api_webhook'),
  edge('svc_stripe', 'api_checkout'),
  edge('svc_stripe', 'api_portal'),
  edge('svc_stripe', 'api_webhook'),
  edge('svc_stripe', 'api_subscription'),
  edge('svc_sendgrid', 'api_contact'),

  // Externes → Cloud / Premium
  edge('svc_firebase', 'util_firebase'),
  edge('svc_drive', 'util_drive'),
  edge('svc_stripe', 'util_pricing'),

  // Utils → Contexts
  edge('util_firebase', 'ctx_auth'),
  edge('util_features', 'ctx_subscription'),
  edge('util_pricing', 'ctx_subscription'),

  // API → Utils
  edge('api_webhook', 'util_firebase'),
  edge('api_signaling', 'util_webrtc'),

  // Hooks → App (orchestration)
  edge('hook_chartstate', 'core_app', true),
  edge('hook_autosave', 'core_app', true),
  edge('hook_modalstate', 'core_app'),
  edge('hook_midihandlers', 'core_app'),
  edge('hook_drivesync', 'core_app'),
  edge('hook_jamsession', 'core_app'),
  edge('hook_chartcrud', 'core_app'),
  edge('hook_audioplayback', 'core_app'),

  // Hooks → Utils/Services sous-jacents
  edge('hook_autosave', 'util_db'),
  edge('hook_autosave', 'util_drive'),
  edge('hook_drivesync', 'util_drive'),
  edge('hook_midihandlers', 'util_midi'),
  edge('hook_jamsession', 'util_webrtc'),
  edge('hook_stripehandlers', 'util_pricing'),
  edge('hook_audioplayback', 'util_audio'),

  // Utils → App
  edge('util_drive', 'core_app'),
  edge('util_firebase', 'core_app'),
  edge('ctx_auth', 'core_app'),
  edge('ctx_subscription', 'core_app'),

  // App → UI
  edge('core_app', 'ui_chartgrid'),
  edge('core_app', 'ui_measuregrid'),
  edge('core_app', 'ui_annotations'),
  edge('core_app', 'ui_mainmenu'),
  edge('core_app', 'ui_gigmenu'),
  edge('core_app', 'ui_auth'),
  edge('core_app', 'ui_upgrade'),
  edge('core_app', 'ui_loadchart'),
  edge('core_app', 'ui_pdf'),
  edge('core_app', 'ui_settings'),
  edge('core_app', 'ui_guitar'),
  edge('core_app', 'ui_midi'),
  edge('core_app', 'ui_transport'),
  edge('core_app', 'ui_jam'),
  edge('core_app', 'ui_collections'),
  edge('core_app', 'ui_subscription_mgmt'),
  edge('core_app', 'ui_mididaw_modal'),

  // MIDI-DAW interne
  edge('ui_mididaw_modal', 'mididaw_components'),
  edge('mididaw_components', 'mididaw_hooks'),
  edge('mididaw_hooks', 'mididaw_core'),
  edge('mididaw_hooks', 'mididaw_audio'),

  // MIDI-DAW ↔ reste de l'app
  edge('ui_mididaw_modal', 'ui_chartgrid'), // pane Chart (lecture seule) + auto-remplissage
  edge('ui_mididaw_modal', 'util_audio'), // mute croisé Editor ↔ DAW
  edge('ui_gigmenu', 'ui_mididaw_modal'), // bouton "Voir la partition"

  // Outils de développement
  edge('core_app', 'dev_arch_explorer'),
  edge('core_app', 'dev_debug_overlay'),
  edge('util_webrtc', 'dev_webrtc_debugger'),

  // Page d'accueil (Landing) → reste de l'app
  edge('core_app', 'ui_landing_desktop'),
  edge('core_app', 'ui_landing_mobile'),
  edge('ui_landing_desktop', 'ui_chartgrid'), // New > Chord Grid
  edge('ui_landing_desktop', 'ui_mididaw_modal'), // New > Piano Rec (dropdown)
  edge('ui_landing_desktop', 'ui_loadchart'), // Open
  edge('ui_landing_desktop', 'ui_collections'), // Setlists
  edge('ui_landing_mobile', 'ui_new_chart_choice'), // New (modal)
  edge('ui_new_chart_choice', 'ui_chartgrid'),
  edge('ui_new_chart_choice', 'ui_mididaw_modal'),
  edge('ui_landing_mobile', 'ui_loadchart'),
  edge('ui_landing_mobile', 'ui_collections'),
];
